/** The size units the portal can produce. See `formatBytes` for why it stops at MB. */
export const BYTE_UNITS = ['bytes', 'kb', 'mb'] as const;

export type ByteUnit = (typeof BYTE_UNITS)[number];

export interface FormattedBytes {
  value: number;
  unit: ByteUnit;
}

const KB = 1_024;
const MB = KB * KB;

/**
 * A byte count as a number and a unit KEY — never as a finished string.
 *
 * The split is the point: "KB" is "ك.ب" in Arabic, so a function returning
 * `'4 KB'` would be shipping untranslatable copy out of a util (U8), and the
 * number itself has to go through `formatNumber` to come out in Latin digits in
 * both locales. The caller owns both halves and this owns the arithmetic.
 *
 * It stops at MB deliberately. Nothing this platform accepts is measured in
 * gigabytes — `media.maxUploadBytes` is single-digit MB — so a GB branch would
 * be a unit no real value can reach, and the first thing it would ever print is
 * a misleading `0.0 GB`.
 */
export function formatBytes(bytes: number): FormattedBytes {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return { value: 0, unit: 'bytes' };
  }

  if (bytes < KB) {
    return { value: Math.round(bytes), unit: 'bytes' };
  }

  const unit: ByteUnit = bytes < MB ? 'kb' : 'mb';
  const scaled = bytes / (unit === 'kb' ? KB : MB);

  // One decimal, and no trailing `.0`: `4 KB` rather than `4.0 KB`.
  return { value: Math.round(scaled * 10) / 10, unit };
}
